import {Component, Injector, Input, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ActivatedRoute, Router} from "@angular/router";
import {AutoSaveService} from "../services/autosave.service";
import {NgOtpInputConfig} from "ng-otp-input";
import {ResumeForm} from "../model/email-form.model";
import {EMPTY} from "rxjs";
import {SpinnerService} from "../core/spinner/spinner.service";
import {ClaimSearchService} from "../services/claim-search.service";
import {AutoSaveForm} from "../model/autosave-form.model";
import {UtilitiesService} from "../utilities/utilities.service";
import {Version} from "../../version";
import {ClaimService} from "../services/claim.service";
import {TranslateService} from "@ngx-translate/core";
import {TransactionInfoService} from "../services/transaction-info.service";
import {GoogleAnalyticsService} from "../services/google-analytics.service";
import {PageService} from "../services/page.service";
import {UiConfigService} from "../ui/ui-config.service";

@Component({
    selector: 'otp-screen',
    templateUrl: './otp-screen.component.html',
    styleUrls: ['./otp-screen.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OtpScreenComponent implements OnInit {
    private autoSaveService: AutoSaveService;
    private claimService: ClaimService;
    private translate: TranslateService;
    private transactionInfoService: TransactionInfoService;
    private googleAnalyticsService: GoogleAnalyticsService;
    private pageService: PageService;
    private uiConfigService: UiConfigService;
    otp: string = '';
    form: ResumeForm = new ResumeForm();
    showOtpError: boolean = false;
    infoMessage: string = '';
    subInfoMessage: string = '';
    otpToken: string = '';
    token: string = '';
    iv: string = '';
    config: NgOtpInputConfig = {
        allowNumbersOnly: true,
        length: 6,
        isPasswordInput: false,
        disableAutoFocus: false,
        placeholder: "",
        inputStyles: {
            margin: "0 12px",
            padding: "6px 0",
            width: "50px !important",
            fontSize: "28px",
            lineHeight: "41px",
            color: "#0A1F44",
            border: 0,
            outline: "none",
            borderBottom: "2px solid #C4CAE0",
        }
    };
    timeLeft: number = 120;
    interval;
    hideTimer = false;

    @Input() maskedEmailAddress;
    @ViewChild('modal_info', {static: true}) private infoModal: TemplateRef<any>;

    constructor(
        private router: Router,
        private modalService: NgbModal,
        private injector: Injector,
        private spinnerService: SpinnerService,
        private claimSearchService: ClaimSearchService,
        private activatedRoute: ActivatedRoute
    ) {
        this.autoSaveService = this.injector.get(AutoSaveService);
        this.claimService = this.injector.get(ClaimService);
        this.translate = this.injector.get(TranslateService);
        this.transactionInfoService = this.injector.get(TransactionInfoService);
        this.googleAnalyticsService = this.injector.get(GoogleAnalyticsService);
        this.pageService = this.injector.get(PageService);
        this.uiConfigService = this.injector.get(UiConfigService);
        const navigation = this.router.currentNavigation();
        const state = navigation?.extras.state as {
            token: string,
            iv: string
        };
        this.token = state.token;
        this.iv = state.iv;
    }

    ngOnInit(): void {
        this.spinnerService.displayLoader(true);
        this.prepareOTP();
        this.resetTimer();
        this.timer();
    }

    prepareOTP() {
        this.claimSearchService.sendOtpForResume(this.token, "email", this.iv)
            .subscribe({
                next: (json) => {
                    this.form = Object.assign(new ResumeForm(), json);
                    if (this.form.getStatus() === 'LOCKED') {
                        this.openInfoModal();
                        this.hideTimer = true;
                        return EMPTY;
                    }
                    this.maskedEmailAddress = this.form.getEmail();
                    this.otpToken = this.form.getToken();
                },
                error: (err:any) => {
                    this.router.navigate(['/error'], {queryParams: {errorType: 'ResumeLinkExpired'}});
                }
            });
    }

    onOtpChange(otp) {
        this.otp = otp;
    }

    timer() {
        clearInterval(this.interval);
        this.interval = setInterval(() => {
            if (this.timeLeft > 0) {
                this.timeLeft--;
            }
        }, 1000);
    }

    resetTimer() {
        this.showOtpError = false;
        clearInterval(this.interval);
        this.timeLeft = 120;
    }

    resendOtp() {
        this.claimSearchService.sendOtpForResume(this.token, "email", this.iv).subscribe((form: ResumeForm) => {
            const resumeForm = Object.assign(new ResumeForm(), form);
            this.otpToken = resumeForm.getToken();
        })
        this.resetTimer();
        this.timer();
    }

    verifyOtp() {
        this.infoMessage = '';
        this.subInfoMessage = '';

        this.injector.get(ClaimSearchService).verifyOtp(this.otpToken, this.otp, "email")
            .subscribe({
                next: (result: any) => {
                    if (result?.status == 'SUCCESS') {
                        this.resumeClaimTransaction(result?.token, this.token, this.iv);
                    } else {
                        if (result?.status == 'LOCKED') {
                            this.hideTimer = true;
                            this.openInfoModal();
                            return EMPTY;
                        }
                        this.showOtpError = true;
                    }
                }
            });
    }


    resumeClaimTransaction(verifiedToken: string, otpToken: string, iv: string) {
        this.autoSaveService.resumeAutoSave(verifiedToken, otpToken, iv).subscribe({
            next: (autoSaveForm: AutoSaveForm) => {
                if (UtilitiesService.isNullOrUndefined(autoSaveForm)) {
                    this.router.navigate(['/error'], {queryParams: {errorType: 'ResumeLinkExpired'}});
                } else {
                    this.autoSaveService.setAutoSaveForm(autoSaveForm);
                    this.claimService.setClaimFromAutoSave(autoSaveForm, otpToken, iv);
                    const claim = this.claimService.getClaim();
                    this.translate.use(claim.getLanguage());
                    this.transactionInfoService.setupAutoSaveTransaction(autoSaveForm);
                    this.googleAnalyticsService.setUpGoogleAnalytics();

                    let autoSaveEntryUrl = this.transactionInfoService.getTransactionInfo().getEntryUrl();
                    if (autoSaveEntryUrl != null) {
                        autoSaveEntryUrl = autoSaveEntryUrl.replace('amp;', '');
                        this.pageService.setEntryUrl(autoSaveEntryUrl);
                    }

                    let autoSaveId = UtilitiesService.isNullOrUndefined(autoSaveForm.getSaveId()) ? '' : autoSaveForm.getSaveId().toString();
                    this.transactionInfoService.getTransactionInfo().setAutoSaveId(autoSaveId);

                    this.pushGAOrigin(true);
                    const country: string = this.transactionInfoService.getTransactionInfo().getRealCountry();
                    this.uiConfigService.setConfigUrl('assets/config/ui.' + country.toLowerCase() + '.qbe.claims.fields.json' + Version.getUrlVersion());
                    this.uiConfigService.loadUiConfig().subscribe(data => {
                        this.router.navigate(['/claimform/getStart'], {relativeTo: this.activatedRoute});
                    })
                }
            },

            error: (err: any) => {
                console.error('resumeClaimTransaction error', err);
                this.router.navigate(['/error'], {queryParams: {errorType: 'ResumeLinkExpired'}});
            }
        });
    }

    isNotNullOrUndefinedStr(val: string) {
        return !UtilitiesService.isNullOrUndefined(val) && val !== '';
    }

    pushGAOrigin(isResume: boolean) {
        let utmSource = this.isNotNullOrUndefinedStr(this.googleAnalyticsService.getUtmSource()) ?
            this.googleAnalyticsService.getUtmSource() : "N/A";
        let utmMedium = this.isNotNullOrUndefinedStr(this.googleAnalyticsService.getUtmMedium()) ?
            this.googleAnalyticsService.getUtmMedium() : "N/A";
        let utmCampaign = this.isNotNullOrUndefinedStr(this.googleAnalyticsService.getUtmCampaign()) ?
            this.googleAnalyticsService.getUtmCampaign() : "N/A";

        if (isResume) {
            // resume save for later
            (<any>window).dataLayer = (<any>window).dataLayer || [];
            (<any>window).dataLayer.push({
                'pageStep': 'Redirect',
                'vPath': '/redirect',
                'utm_source': utmSource,
                'utm_medium': utmMedium,
                'utm_campaign': utmCampaign,
                'event': 'vpageview',
                'ecommerce': {
                    'checkout': {'actionField': {'step': 1, 'option': 'SaveResume'}}
                }
            });
        } else {
            // transaction from agent channel ie. qnect
            let transactionInfo = this.transactionInfoService.getTransactionInfo();
            let pageStep = 'QnectRedirect';
            let vPath = '/qnectredirect';
            let actionStep = 2;
            let policyNumber = transactionInfo.getPolicyNumber();
            if (transactionInfo.getNumberOfRisks() > 1 && transactionInfo.getPolicyType() === 'motor') {
                pageStep = 'QnectRedirectAuthentication';
                vPath = '/qnectredirectauthentication';
                actionStep = 1;
            }
            (<any>window).dataLayer = (<any>window).dataLayer || [];
            (<any>window).dataLayer.push({
                'pageStep': pageStep,
                'vPath': vPath,
                'utm_source': utmSource,
                'utm_medium': utmMedium,
                'utm_campaign': utmCampaign,
                'event': 'vpageview',
                'PolicyNo': policyNumber,
                'ecommerce': {
                    'checkout': {'actionField': {'step': actionStep, 'option': 'Qnectlodge'}}
                }
            });
        }
    }

    openInfoModal(error?:string) {
        if(error){
            this.infoMessage = "registration.genericError";
            this.subInfoMessage = "pagePaymentAndContact.fps.unableToVerify";
        }else{
            this.infoMessage = "motorClaim.homePage.emailAddressLocked";
            this.subInfoMessage = "motorClaim.homePage.emailAddressLockedOtp";
        }
        this.modalService.open(this.infoModal, {size: 'md', backdrop: 'static', keyboard: false});
    }

}